/**
 * Phase 5 — Specialty Restaurants table.
 *
 * One row per service: for each specialty restaurant in
 * `meal_plan.specialty_restaurants[]`, we render one row per entry in its
 * `services[]` array. Columns:
 *   Restaurant | Meal | Open | Start | End | Special note
 *
 * Brunch activation (UX rule): when a restaurant has a `brunch` service
 * and the user toggles it ON, we automatically flip that restaurant's same-
 * day `lunch` service to closed and show a hint on the lunch row. The data
 * layer allows both states to coexist; this is purely a guard rail in the
 * editor.
 *
 * Backend constraint: when open=true the editor pre-fills start + end from
 * the restaurant's library `default_times` so the user can't accidentally
 * 422 themselves.
 */
import { useMemo } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import FestivalsEditor from "@/components/mealplan/FestivalsEditor";

const SPECIALTY_COLSPAN = 6;

const DEFAULT_TIME_BY_MEAL = {
  embarkation_breakfast: { start: "06:00", end: "08:00" },
  breakfast: { start: "07:00", end: "10:00" },
  brunch: { start: "10:30", end: "13:00" },
  lunch: { start: "12:00", end: "14:30" },
  embarkation_lunch: { start: "11:00", end: "15:00" },
  embarkation_dinner: { start: "17:00", end: "20:00" },
  dinner: { start: "18:00", end: "22:00" },
};

// Friendly labels for the "Meal" column. Falls back to the raw meal key
// (capitalized) when not in the map.
const MEAL_LABELS = {
  embarkation_breakfast: "Embark. Breakfast",
  breakfast: "Breakfast",
  brunch: "Brunch",
  lunch: "Lunch",
  embarkation_lunch: "Embarkation Lunch",
  embarkation_dinner: "Embarkation Dinner",
  dinner: "Dinner",
};

const MEAL_ORDER = [
  "embarkation_breakfast", "breakfast", "brunch", "lunch",
  "embarkation_lunch", "embarkation_dinner", "dinner",
];

// Phase 13 — list of Day-1-only embarkation meal keys. Used to render
// the small amber "Day 1" pill next to the meal label.
const EMBARK_MEAL_KEYS = new Set([
  "embarkation_breakfast",
  "embarkation_lunch",
  "embarkation_dinner",
]);

export default function SpecialtyRestaurantsTable({
  value, onChange, restaurantsLib,
  // Phase 7++ — festivals library + venue opt-in via lib.hosts_festivals.
  festivalsLib,
  // Phase 13 — Day-1-only embark gate. When `dayNumber !== 1` we skip
  // any service whose meal is one of EMBARK_MEAL_KEYS. Backend silently
  // accepts those on any day, but the editor must never expose them
  // mid-cruise.
  dayNumber = 1,
}) {
  const libById = useMemo(() => {
    const m = {};
    for (const r of restaurantsLib || []) m[r.id] = r;
    return m;
  }, [restaurantsLib]);

  const rows = value || [];

  const patchService = (rid, meal, patch) => {
    onChange(
      rows.map((rr) => {
        if (rr.restaurant_id !== rid) return rr;
        return {
          ...rr,
          services: rr.services.map((s) =>
            s.meal === meal ? { ...s, ...patch } : s
          ),
        };
      })
    );
  };

  // Phase 7++ — patch venue-level fields (used by the Festivals editor).
  const patchVenue = (rid, patch) => {
    onChange(
      rows.map((rr) =>
        rr.restaurant_id === rid ? { ...rr, ...patch } : rr
      )
    );
  };

  // Brunch activation: when brunch is being turned ON, also close the lunch
  // service on the same restaurant. When brunch is being turned OFF, the
  // lunch row stays as the user left it (we don't re-open it automatically).
  const toggleService = (rid, meal, open) => {
    onChange(
      rows.map((rr) => {
        if (rr.restaurant_id !== rid) return rr;
        const lib = libById[rid];
        const defaults = lib?.default_times || {};
        return {
          ...rr,
          services: rr.services.map((s) => {
            if (s.meal === meal) {
              const next = { ...s, open };
              if (open && (!s.start || !s.end)) {
                const d = defaults[meal] || DEFAULT_TIME_BY_MEAL[meal] || { start: "", end: "" };
                next.start = s.start || d.start;
                next.end = s.end || d.end;
              }
              return next;
            }
            if (meal === "brunch" && s.meal === "lunch" && open) {
              return { ...s, open: false };
            }
            return s;
          }),
        };
      })
    );
  };

  return (
    <div
      className="overflow-x-auto rounded-md border bg-white"
      data-testid="specialty-restaurants-table"
    >
      <table className="w-full border-collapse text-xs">
        <thead>
          <tr className="bg-slate-100">
            <th className="border-b px-3 py-2 text-left font-bold uppercase tracking-wider">
              Restaurant
            </th>
            <th className="border-b border-l px-3 py-2 text-left font-bold uppercase tracking-wider w-[100px]">
              Meal
            </th>
            <th className="border-b border-l px-3 py-2 text-center font-bold uppercase tracking-wider w-[80px]">
              Open
            </th>
            <th className="border-b border-l px-3 py-2 text-left font-bold uppercase tracking-wider w-[100px]">
              Start
            </th>
            <th className="border-b border-l px-3 py-2 text-left font-bold uppercase tracking-wider w-[100px]">
              End
            </th>
            <th className="border-b border-l px-3 py-2 text-left font-bold uppercase tracking-wider">
              Special note
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td colSpan={6} className="px-3 py-8 text-center text-muted-foreground">
                No specialty restaurants — add some in Settings → Restaurants.
              </td>
            </tr>
          )}
          {rows.flatMap((row) => {
            const lib = libById[row.restaurant_id];
            // Phase 13 — Day-1-only embark gate. On Day 2+ the editor
            // hides any service whose meal is an embark slot, even if
            // the backend round-trips it (silent-accept design).
            const visibleServices = (row.services || []).filter((svc) =>
              dayNumber === 1 || !EMBARK_MEAL_KEYS.has(svc.meal)
            );
            const services = [...visibleServices].sort(
              (a, b) =>
                MEAL_ORDER.indexOf(a.meal) - MEAL_ORDER.indexOf(b.meal)
            );
            const brunchActive = services.some(
              (s) => s.meal === "brunch" && s.open
            );
            const serviceRows = services.map((svc, idx) => {
              const isFirst = idx === 0;
              const lunchSuperseded =
                brunchActive && svc.meal === "lunch" && !svc.open;
              return (
                <tr
                  key={`${row.restaurant_id}-${svc.meal}`}
                  data-testid={`specrest-row-${row.restaurant_id}-${svc.meal}`}
                  className={cn(
                    "border-b last:border-b-0",
                    svc.open && "bg-emerald-50/40",
                    !svc.open && "bg-gray-50/40"
                  )}
                >
                  {isFirst ? (
                    <td
                      rowSpan={services.length}
                      className="border-r bg-slate-50 px-3 py-2 align-top"
                    >
                      <div className="font-semibold">
                        {/* Phase 7 — fall back to snapshot if the lib doc
                            has been deleted/replaced. Stale rows still
                            render with the saved name + a small warning. */}
                        {lib?.name || row.restaurant_name || "(unnamed)"}
                      </div>
                      {!lib && (
                        <div className="mt-0.5 inline-flex items-center rounded-full bg-amber-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-amber-900">
                          Stale lib reference
                        </div>
                      )}
                      {lib?.fixed_menu_label && (
                        <div className="mt-0.5 text-[10px] italic text-muted-foreground">
                          {lib.fixed_menu_label}
                        </div>
                      )}
                    </td>
                  ) : null}
                  <td className="px-3 py-2 align-middle">
                    <span className="text-xs font-semibold capitalize">
                      {MEAL_LABELS[svc.meal] || svc.meal}
                    </span>
                    {EMBARK_MEAL_KEYS.has(svc.meal) && (
                      <span
                        className="ml-2 inline-flex items-center rounded-full bg-amber-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-amber-900"
                        data-testid={`specrest-embark-pill-${row.restaurant_id}-${svc.meal}`}
                      >
                        Day 1
                      </span>
                    )}
                    {svc.meal === "brunch" && (
                      <span
                        className={cn(
                          "ml-2 inline-flex items-center rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider",
                          svc.open
                            ? "bg-violet-100 text-violet-800"
                            : "bg-gray-100 text-gray-500"
                        )}
                      >
                        {svc.open ? "Brunch ON" : "Brunch off"}
                      </span>
                    )}
                  </td>
                  <td className="border-l px-3 py-2 text-center align-middle">
                    {svc.meal === "brunch" ? (
                      <Switch
                        checked={!!svc.open}
                        onCheckedChange={(c) =>
                          toggleService(row.restaurant_id, svc.meal, c)
                        }
                        data-testid={`specrest-brunch-activate-${row.restaurant_id}`}
                      />
                    ) : (
                      <Switch
                        checked={!!svc.open}
                        onCheckedChange={(c) =>
                          toggleService(row.restaurant_id, svc.meal, c)
                        }
                        data-testid={`specrest-open-${row.restaurant_id}-${svc.meal}`}
                      />
                    )}
                  </td>
                  <td className="border-l px-3 py-2 align-middle">
                    {svc.open ? (
                      <Input
                        type="time"
                        value={svc.start || ""}
                        onChange={(e) =>
                          patchService(row.restaurant_id, svc.meal, {
                            start: e.target.value,
                          })
                        }
                        className="h-7 font-mono text-xs"
                        data-testid={`specrest-start-${row.restaurant_id}-${svc.meal}`}
                      />
                    ) : (
                      <span className="text-[10px] italic text-muted-foreground">
                        —
                      </span>
                    )}
                  </td>
                  <td className="border-l px-3 py-2 align-middle">
                    {svc.open ? (
                      <Input
                        type="time"
                        value={svc.end || ""}
                        onChange={(e) =>
                          patchService(row.restaurant_id, svc.meal, {
                            end: e.target.value,
                          })
                        }
                        className="h-7 font-mono text-xs"
                        data-testid={`specrest-end-${row.restaurant_id}-${svc.meal}`}
                      />
                    ) : (
                      <span className="text-[10px] italic text-muted-foreground">
                        —
                      </span>
                    )}
                  </td>
                  <td className="border-l px-3 py-2 align-middle">
                    <Input
                      value={svc.special_note || ""}
                      onChange={(e) =>
                        patchService(row.restaurant_id, svc.meal, {
                          special_note: e.target.value,
                        })
                      }
                      placeholder={
                        lunchSuperseded
                          ? "Lunch closed (brunch active)"
                          : "Chef's Table tonight…"
                      }
                      disabled={lunchSuperseded}
                      className={cn(
                        "h-7 text-xs",
                        lunchSuperseded && "italic text-violet-700"
                      )}
                      data-testid={`specrest-note-${row.restaurant_id}-${svc.meal}`}
                    />
                  </td>
                </tr>
              );
            });
            // Phase 7++ — venue-level Festivals editor row, only when the
            // lib doc opted in via `hosts_festivals=true`. Spans the full
            // table width and lives directly under the venue's last
            // service row.
            const festivalsRow =
              lib?.hosts_festivals ? (
                <tr
                  key={`${row.restaurant_id}-festivals`}
                  data-testid={`specrest-festivals-row-${row.restaurant_id}`}
                >
                  <td
                    colSpan={SPECIALTY_COLSPAN}
                    className="border-b bg-rose-50/20 px-3 py-2"
                  >
                    <FestivalsEditor
                      value={row.festivals || []}
                      onChange={(festivals) =>
                        patchVenue(row.restaurant_id, { festivals })
                      }
                      festivalsLib={festivalsLib}
                      tidPrefix={`specrest-${row.restaurant_id}`}
                    />
                  </td>
                </tr>
              ) : null;
            return [...serviceRows, festivalsRow].filter(Boolean);
          })}
        </tbody>
      </table>
    </div>
  );
}
